import { apiSlice } from "../store/api/adminApi"

export interface OrganizerAssignment {
  id: number;
  organizerId: number;
  organizerName: string;
  organizerPhone?: string;
  commissionValue: number;
  commissionType: 'fixed' | 'percent';
  referralCommission: number;
  startDate?: string;
  endDate?: string;
  isDisabled: boolean;
  isPaid: boolean;
  isReferralPaid: boolean;
  referralPaidAt?: string | null;
  createdAt: string;
  totalFee?: number;
  totalAmount?: number;
  totalCommission?: number;
}

export interface MarketingPerson {
  id: number;
  userId: number;
  name: string;
  phone: string;
  IDcode?: string;
  organizersCount: number;
  isDisabled: boolean;
  createdAt?: string;
  assignmentsAll: number;
  assignmentsActive: number;
  assignmentsFinished: number;
  hasUnpaidAssignment: boolean;
  hasUnpaidReferral: boolean;
}

export interface MarketingPersonReportItem {
  marketingPersonId: number;
  marketingPersonName: string;
  marketingPersonPhone: string;
  workStartDate?: string;
  workEndDate?: string;
  assignmentCreated: string;
  organizerId: number;
  organizerName: string;
  ticketCount: number;
  totalAmount: number;
  totalFee: number;
  totalCommission: number;
  commissionValue: number;
  commissionType: string;
  eventsCount: number;
  isDisabled: boolean;
  isReferralPaid: boolean;
  referralCommission: number;
}

export interface MarketingPersonEventReportItem {
  marketingPersonId: number;
  marketingPersonName: string;
  marketingPersonPhone: string;
  organizerId: number;
  organizerName: string;
  eventId: number;
  eventName: string;
  eventStartDate?: string;
  eventEndDate?: string;
  ticketCount: number;
  totalAmount: number;
  totalFee: number;
  totalCommission: number;
  commissionValue: number;
  commissionType: string;
  workStartDate?: string;
  workEndDate?: string;
  isPaid: boolean;
}

export interface DetailsSummary {
  ticketsSold: number;
  totalAmount: number;
  totalFee: number;
  totalCommission: number;
  paidCommission: number;
  unpaidCommission: number;
  totalReferralCommission: number;
  paidReferralCommission: number;
  unpaidReferralCommission: number;
  organizersCount: number;
  eventsCount: number;
  paymentsCount: number;
}

export interface DetailsOrganizerRow {
  assignmentId: number;
  organizerId: number;
  organizerUserId: number;
  organizerName: string;
  organizerPhone: string;
  ticketsSold: number;
  totalAmount: number;
  totalFee: number;
  totalCommission: number;
  eventsCount: number;
  commissionValue: number;
  commissionType: 'fixed' | 'percent';
  referralCommission: number;
  workStartDate?: string;
  workEndDate?: string;
  isReferralPaid: boolean;
  referralPaidAt?: string | null;
  commentsCount: number;
}

export interface DetailsEventRow {
  eventId: number;
  eventName: string;
  eventStartDate: string;
  eventEndDate: string;
  organizerId: number;
  organizerUserId: number;
  organizerName: string;
  ticketsSold: number;
  totalAmount: number;
  totalFee: number;
  totalCommission: number;
  commissionValue: number;
  commissionType: 'fixed' | 'percent';
  isPaid: boolean;
  paidAt?: string | null;
}

export type PaymentKind = 'event' | 'organizer' | 'referral';

export interface Payment {
  id: number;
  marketingPersonId: number;
  marketingPersonName: string;
  marketingPersonPhone: string;
  organizerId: number;
  organizerName: string;
  eventId?: number | null;
  eventName?: string | null;
  eventEndDate?: string | null;
  kind: PaymentKind;
  month: string;
  amount: number;
  isPaid: boolean;
  paidAt?: string | null;
  note?: string | null;
  createDate: string;
  updateDate: string;
}

export interface PaymentsSummary {
  count: number;
  totalAmount: number;
  eventPaymentsCount: number;
  organizerPaymentsCount: number;
}

export interface PaymentsReport {
  summary: PaymentsSummary;
  payments: Payment[];
}

export interface CreatePaymentPayload {
  kind: PaymentKind;
  month: string;
  amount?: number;
  eventId?: number;
  organizerId?: number;
  note?: string;
}

export interface PaymentListFilter {
  marketingPersonId?: number;
  organizerId?: number;
  eventId?: number;
  kind?: PaymentKind;
  monthFrom?: string;
  monthTo?: string;
  search?: string;
}

export interface MarketingPersonDetails {
  summary: DetailsSummary;
  organizers: DetailsOrganizerRow[];
  events: DetailsEventRow[];
  payments: Payment[];
}

export interface CreateMarketingPersonPayload {
  phoneNumber: string;
}

export interface AddOrganizerAssignmentPayload {
  organizerId: number;
  commissionValue?: number;
  commissionType?: 'fixed' | 'percent';
  referralCommission?: number;
  startDate?: string;
  endDate?: string;
}

export interface UpdateOrganizerAssignmentPayload {
  commissionValue?: number;
  commissionType?: 'fixed' | 'percent';
  referralCommission?: number;
  startDate?: string;
  endDate?: string;
  isDisabled?: boolean;
  isPaid?: boolean;
  isReferralPaid?: boolean;
}

export interface UpdateMarketingPersonPayload {
  isDisabled?: boolean;
}

export interface MarketingPersonReportFilter {
  marketingPersonId?: number;
  startPeriod?: string;
  endPeriod?: string;
  search?: string;
}

export interface MarketingPersonDetailsFilter {
  marketingPersonId: number;
  startPeriod?: string;
  endPeriod?: string;
  organizerFilter?: 'all' | 'paid' | 'unpaid';
  eventFilter?: 'all' | 'paid' | 'unpaid';
  search?: string;
}

export interface SearchUserResult {
  id: number;
  name: string;
  phone: string;
  isNew: boolean;
}

export interface SearchUserListItem {
  id: number;
  name: string;
  phone: string;
  /** Organizer search only: marketing persons this organizer is already bound to. */
  marketingPersons?: OrganizerMarketingPersonChip[];
}

// ── Payouts (snapshot-based) ──────────────────────────────────────────────────
export type PayoutStatus = 'draft' | 'confirmed' | 'cancelled';
export type PayoutItemKind = 'event' | 'referral';

export interface PayoutPreviewEvent {
  eventId: number;
  eventName: string;
  eventEndDate: string;
  organizerId: number;
  organizerUserId: number;
  organizerName: string;
  organizerPhone: string;
  assignmentId: number;
  ticketsSold: number;
  totalSales: number;
  totalFees: number;
  commission: number;
  commissionValue: number;
  commissionType: string;
  alreadyPaid: boolean;
  paidInPayoutId?: number | null;
}

export interface PayoutPreviewEventsTotals {
  totalTickets: number;
  totalSales: number;
  totalFees: number;
  totalCommission: number;
  eventsCount: number;
}

export interface PayoutPreviewEventsResponse {
  events: PayoutPreviewEvent[];
  totals: PayoutPreviewEventsTotals;
}

export interface PayoutReferralPreview {
  assignmentId: number;
  organizerId: number;
  organizerUserId: number;
  organizerName: string;
  organizerPhone: string;
  referralCommission: number;
  totalSalesAllTime: number;
  totalFeesAllTime: number;
  totalSales60Days: number;
  totalFees60Days: number;
}

export interface PayoutReferralsResponse {
  organizers: PayoutReferralPreview[];
  totalReferralCommission: number;
}

/** Money already paid to a different marketing person for a shared organizer. */
export interface OtherMarketingPersonPayment {
  organizerId: number;
  organizerName: string;
  marketingPersonId: number;
  marketingPersonName: string;
  marketingPersonPhone: string;
  payoutId: number | null;
  paidAt: string;
  eventIds: number[];
  eventCommission: number;
  referralCommission: number;
}

export interface CreatePayoutPayload {
  dateFrom: string;
  dateTo: string;
  eventItems?: { eventId: number }[];
  referralItems?: { assignmentId: number }[];
  note?: string;
}

export interface PayoutItem {
  id: number;
  kind: PayoutItemKind;
  organizerId?: number | null;
  eventId?: number | null;
  assignmentId?: number | null;
  organizerName?: string | null;
  organizerPhone?: string | null;
  eventName?: string | null;
  eventEndDate?: string | null;
  ticketsSold: number;
  totalSales: number;
  totalFees: number;
  amount: number;
  salesLast60Days: number;
  feesLast60Days: number;
  commissionType?: string | null;
  commissionValue: number;
}

export interface PayoutSummary {
  id: number;
  marketingPersonId: number;
  marketingPersonName: string;
  marketingPersonPhone: string;
  dateFrom?: string | null;
  dateTo?: string | null;
  status: PayoutStatus;
  totalEventCommission: number;
  totalReferralCommission: number;
  totalAmount: number;
  totalTickets: number;
  totalSales: number;
  totalFees: number;
  note?: string | null;
  createdById?: number | null;
  createdByName?: string | null;
  createdByPhone?: string | null;
  eventItemsCount: number;
  referralItemsCount: number;
  createDate: string;
  updateDate: string;
}

export interface PayoutDetails extends PayoutSummary {
  items: PayoutItem[];
}

export interface PayoutListFilter {
  marketingPersonId?: number;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface AssignmentListItem {
  id: number;
  marketingPersonId: number;
  marketingPersonName: string;
  marketingPersonPhone: string;
  organizerId: number;
  organizerName: string;
  commissionValue: number;
  commissionType: 'fixed' | 'percent';
  referralCommission: number;
  startDate?: string;
  endDate?: string;
  isDisabled: boolean;
  isPaid: boolean;
  isReferralPaid: boolean;
  referralPaidAt?: string | null;
  createdAt: string;
  totalFee: number;
  totalAmount: number;
  totalCommission: number;
}

export interface OrganizerComment {
  id: number;
  marketingPersonId: number;
  organizerId: number;
  text: string;
  createdByName: string;
  createdByPhone?: string | null;
  createDate: string;
}

export interface OrganizerMarketingPersonChip {
  assignmentId: number;
  marketingPersonId: number;
  name: string;
  phone: string;
  isDisabled: boolean;
}

export const marketingPersonApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getMarketingPersonsList: build.query<MarketingPerson[], { search?: string; status?: string } | void>({
      query: (params) => {
        const urlParams = new URLSearchParams();
        if (params && params.search) urlParams.append('search', params.search);
        if (params && params.status && params.status !== 'all') urlParams.append('status', params.status);
        const queryString = urlParams.toString();
        return {
          url: `/admin/marketing-persons/list${queryString ? `?${queryString}` : ''}`,
        };
      },
    }),
    getAssignmentsList: build.query<AssignmentListItem[], { status?: string } | void>({
      query: (params) => {
        const urlParams = new URLSearchParams();
        if (params && params.status && params.status !== 'all') urlParams.append('status', params.status);
        const queryString = urlParams.toString();
        return {
          url: `/admin/marketing-persons/assignments${queryString ? `?${queryString}` : ''}`,
        };
      },
    }),
    getPersonAssignments: build.query<OrganizerAssignment[], { marketingPersonId: number; status?: string }>({
      query: ({ marketingPersonId, status }) => {
        const urlParams = new URLSearchParams();
        if (status && status !== 'all') urlParams.append('status', status);
        const queryString = urlParams.toString();
        return {
          url: `/admin/marketing-persons/${marketingPersonId}/assignments${queryString ? `?${queryString}` : ''}`,
        };
      },
    }),
    getMarketingPersonDetails: build.query<MarketingPersonDetails, MarketingPersonDetailsFilter>({
      query: ({ marketingPersonId, ...rest }) => {
        const urlParams = new URLSearchParams();
        if (rest.startPeriod) urlParams.append('startPeriod', rest.startPeriod);
        if (rest.endPeriod) urlParams.append('endPeriod', rest.endPeriod);
        if (rest.organizerFilter && rest.organizerFilter !== 'all') urlParams.append('organizerFilter', rest.organizerFilter);
        if (rest.eventFilter && rest.eventFilter !== 'all') urlParams.append('eventFilter', rest.eventFilter);
        if (rest.search) urlParams.append('search', rest.search);
        const qs = urlParams.toString();
        return { url: `/admin/marketing-persons/${marketingPersonId}/details${qs ? `?${qs}` : ''}` };
      },
    }),
    searchUserByPhone: build.mutation<SearchUserResult, { phoneNumber: string }>({
      query: ({ phoneNumber }) => ({
        url: `/admin/marketing-persons/search-user`,
        method: 'POST',
        body: { phoneNumber },
      }),
    }),
    searchUsersList: build.mutation<SearchUserListItem[], { search: string }>({
      query: ({ search }) => ({
        url: `/admin/marketing-persons/search-users-list`,
        method: 'POST',
        body: { search },
      }),
    }),
    searchOrganizersList: build.mutation<SearchUserListItem[], { search: string }>({
      query: ({ search }) => ({
        url: `/admin/marketing-persons/search-organizers-list?search=${encodeURIComponent(search)}`,
        method: 'GET',
      }),
    }),
    addMarketingPerson: build.mutation<any, { body: CreateMarketingPersonPayload; language: string }>({
      query: ({ body, language }) => ({
        url: `/admin/marketing-persons?language=${language}`,
        method: 'POST',
        body,
      }),
    }),
    addOrganizerAssignment: build.mutation<OrganizerAssignment, { marketingPersonId: number; body: AddOrganizerAssignmentPayload; language: string }>({
      query: ({ marketingPersonId, body, language }) => ({
        url: `/admin/marketing-persons/${marketingPersonId}/assignment?language=${language}`,
        method: 'POST',
        body,
      }),
    }),
    updateOrganizerAssignment: build.mutation<OrganizerAssignment, { assignmentId: number; body: UpdateOrganizerAssignmentPayload; language: string }>({
      query: ({ assignmentId, body, language }) => ({
        url: `/admin/marketing-persons/assignment/${assignmentId}?language=${language}`,
        method: 'PUT',
        body,
      }),
    }),
    createMarketingPayment: build.mutation<Payment, { marketingPersonId: number; body: CreatePaymentPayload; language: string }>({
      query: ({ marketingPersonId, body, language }) => ({
        url: `/admin/marketing-persons/${marketingPersonId}/payment?language=${language}`,
        method: 'POST',
        body,
      }),
    }),
    deleteMarketingPayment: build.mutation<void, { paymentId: number; language: string }>({
      query: ({ paymentId, language }) => ({
        url: `/admin/marketing-persons/payment/${paymentId}?language=${language}`,
        method: 'DELETE',
      }),
    }),
    listMarketingPayments: build.query<Payment[], PaymentListFilter>({
      query: (filters) => {
        const p = new URLSearchParams();
        if (filters.marketingPersonId) p.append('marketingPersonId', String(filters.marketingPersonId));
        if (filters.organizerId) p.append('organizerId', String(filters.organizerId));
        if (filters.eventId) p.append('eventId', String(filters.eventId));
        if (filters.kind) p.append('kind', filters.kind);
        if (filters.monthFrom) p.append('monthFrom', filters.monthFrom);
        if (filters.monthTo) p.append('monthTo', filters.monthTo);
        if (filters.search) p.append('search', filters.search);
        const qs = p.toString();
        return { url: `/admin/marketing-persons/payments${qs ? `?${qs}` : ''}` };
      },
    }),
    getMarketingPaymentsReport: build.query<PaymentsReport, PaymentListFilter>({
      query: (filters) => {
        const p = new URLSearchParams();
        if (filters.marketingPersonId) p.append('marketingPersonId', String(filters.marketingPersonId));
        if (filters.organizerId) p.append('organizerId', String(filters.organizerId));
        if (filters.eventId) p.append('eventId', String(filters.eventId));
        if (filters.kind) p.append('kind', filters.kind);
        if (filters.monthFrom) p.append('monthFrom', filters.monthFrom);
        if (filters.monthTo) p.append('monthTo', filters.monthTo);
        if (filters.search) p.append('search', filters.search);
        const qs = p.toString();
        return { url: `/admin/marketing-persons/payments/report${qs ? `?${qs}` : ''}` };
      },
    }),
    removeOrganizerAssignment: build.mutation<void, { assignmentId: number; language: string }>({
      query: ({ assignmentId, language }) => ({
        url: `/admin/marketing-persons/assignment/${assignmentId}?language=${language}`,
        method: 'DELETE',
      }),
    }),
    getOrganizerComments: build.query<OrganizerComment[], { assignmentId: number; language: string }>({
      query: ({ assignmentId, language }) => ({
        url: `/admin/marketing-persons/assignment/${assignmentId}/comments?language=${language}`,
      }),
    }),
    addOrganizerComment: build.mutation<OrganizerComment, { assignmentId: number; text: string; language: string }>({
      query: ({ assignmentId, text, language }) => ({
        url: `/admin/marketing-persons/assignment/${assignmentId}/comments?language=${language}`,
        method: 'POST',
        body: { text },
      }),
    }),
    deleteOrganizerComment: build.mutation<void, { commentId: number; language: string }>({
      query: ({ commentId, language }) => ({
        url: `/admin/marketing-persons/comments/${commentId}?language=${language}`,
        method: 'DELETE',
      }),
    }),
    updateMarketingPerson: build.mutation<any, { id: number; body: UpdateMarketingPersonPayload; language: string }>({
      query: ({ id, body, language }) => ({
        url: `/admin/marketing-persons/${id}?language=${language}`,
        method: 'PUT',
        body,
      }),
    }),
    deleteMarketingPerson: build.mutation<void, { id: number; language: string }>({
      query: ({ id, language }) => ({
        url: `/admin/marketing-persons/${id}?language=${language}`,
        method: 'DELETE',
      }),
    }),
    getMarketingPersonsReport: build.query<MarketingPersonReportItem[], MarketingPersonReportFilter>({
      query: (filters) => {
        const params = new URLSearchParams();
        if (filters.marketingPersonId) params.append('marketingPersonId', String(filters.marketingPersonId));
        if (filters.startPeriod) params.append('startPeriod', filters.startPeriod);
        if (filters.endPeriod) params.append('endPeriod', filters.endPeriod);
        if (filters.search) params.append('search', filters.search);
        return {
          url: `/admin/marketing-persons/report?${params.toString()}`,
        };
      },
    }),
    getMarketingPersonsReportByEvents: build.query<MarketingPersonEventReportItem[], MarketingPersonReportFilter>({
      query: (filters) => {
        const params = new URLSearchParams();
        if (filters.marketingPersonId) params.append('marketingPersonId', String(filters.marketingPersonId));
        if (filters.startPeriod) params.append('startPeriod', filters.startPeriod);
        if (filters.endPeriod) params.append('endPeriod', filters.endPeriod);
        if (filters.search) params.append('search', filters.search);
        return {
          url: `/admin/marketing-persons/report/by-events?${params.toString()}`,
        };
      },
    }),
    previewPayoutEvents: build.query<PayoutPreviewEventsResponse, { marketingPersonId: number; dateFrom: string; dateTo: string }>({
      query: ({ marketingPersonId, dateFrom, dateTo }) => ({
        url: `/admin/marketing-persons/${marketingPersonId}/payout-preview/events?dateFrom=${encodeURIComponent(dateFrom)}&dateTo=${encodeURIComponent(dateTo)}`,
      }),
    }),
    previewPayoutReferrals: build.query<PayoutReferralsResponse, { marketingPersonId: number }>({
      query: ({ marketingPersonId }) => ({
        url: `/admin/marketing-persons/${marketingPersonId}/payout-preview/referrals`,
      }),
    }),
    previewPayoutOtherPayments: build.query<OtherMarketingPersonPayment[], { marketingPersonId: number }>({
      query: ({ marketingPersonId }) => ({
        url: `/admin/marketing-persons/${marketingPersonId}/payout-preview/other-payments`,
      }),
    }),
    createPayout: build.mutation<PayoutDetails, { marketingPersonId: number; body: CreatePayoutPayload; language: string }>({
      query: ({ marketingPersonId, body, language }) => ({
        url: `/admin/marketing-persons/${marketingPersonId}/payouts?language=${language}`,
        method: 'POST',
        body,
      }),
    }),
    listPayouts: build.query<PayoutSummary[], PayoutListFilter>({
      query: (filters) => {
        const p = new URLSearchParams();
        if (filters.marketingPersonId) p.append('marketingPersonId', String(filters.marketingPersonId));
        if (filters.dateFrom) p.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) p.append('dateTo', filters.dateTo);
        if (filters.search) p.append('search', filters.search);
        const qs = p.toString();
        return { url: `/admin/marketing-persons/payouts${qs ? `?${qs}` : ''}` };
      },
    }),
    getPayout: build.query<PayoutDetails, { payoutId: number }>({
      query: ({ payoutId }) => ({ url: `/admin/marketing-persons/payouts/${payoutId}` }),
    }),
    deletePayout: build.mutation<void, { payoutId: number; language: string }>({
      query: ({ payoutId, language }) => ({
        url: `/admin/marketing-persons/payouts/${payoutId}?language=${language}`,
        method: 'DELETE',
      }),
    }),
  }),
});
